import React, { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
// Define user types
export type UserRole = 'msme' | 'investor' | 'shariah' | 'trustee' | 'digital-advisor' | 'rating-agency' | 'admin';
export interface User {
  email: string;
  name: string;
  role: UserRole;
  organization?: string;
}
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Demo user credentials
const DEMO_USERS: Record<string, User> = {
  'msme@example.com': {
    email: 'msme@example.com',
    name: 'Amani Cafe',
    role: 'msme',
    organization: 'Amani Cafe'
  },
  'investor@example.com': {
    email: 'investor@example.com',
    name: 'Sarah Abdullah',
    role: 'investor',
    organization: 'Green Impact Fund'
  },
  'shariah@example.com': {
    email: 'shariah@example.com',
    name: 'Dr. Fatimah Ismail',
    role: 'shariah',
    organization: 'Shariah Advisory Board'
  },
  'trustee@example.com': {
    email: 'trustee@example.com',
    name: 'Mohd Rizal',
    role: 'trustee',
    organization: 'Trust Securities Bhd'
  },
  'advisor@example.com': {
    email: 'advisor@example.com',
    name: 'Ahmad Bakri',
    role: 'digital-advisor',
    organization: 'CMS Finance Platform'
  },
  'rating@example.com': {
    email: 'rating@example.com',
    name: 'Nurul Huda',
    role: 'rating-agency',
    organization: 'Malaysian Rating Corporation'
  }
};
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  // Check for saved auth on initial load
  useEffect(() => {
    const savedUser = localStorage.getItem('auth_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);
  const login = async (email: string, password: string): Promise<boolean> => {
    // Demo login logic - in a real app, this would be an API call
    if (DEMO_USERS[email] && password === 'Fc6wiINFrx') {
      const loggedInUser = DEMO_USERS[email];
      setUser(loggedInUser);
      localStorage.setItem('auth_user', JSON.stringify(loggedInUser));
      // Redirect based on role
      switch (loggedInUser.role) {
        case 'msme':
          navigate('/dashboard/msme');
          break;
        case 'investor':
          navigate('/dashboard/investor');
          break;
        case 'shariah':
          navigate('/dashboard/shariah');
          break;
        case 'trustee':
          navigate('/dashboard/trustee');
          break;
        case 'digital-advisor':
          navigate('/dashboard/digital-advisor');
          break;
        case 'rating-agency':
          navigate('/dashboard/rating-agency');
          break;
        default:
          navigate('/dashboard');
      }
      return true;
    }
    return false;
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
    navigate('/');
  };
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    isAuthenticated: !!user
  }} data-id="element-130">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};